// Internal modules
import type { ReactNode } from 'react';
import { appStore } from '@/store';
import {
  features,
  getContent,
  getShippingAddress,
  getShippingCountry,
  getSimpleShippingAddressString,
  logger,
  simplifyShippingAddress,
} from '@/lib';
import { calculateShippingAndTaxes } from './graphql/operations/calculateShippingAndTaxes';
// @ts-expect-error
import { updateShippingOptions } from './graphql/operations/updateShippingOptions';
// @ts-expect-error
import { logFetchingShippingMethodsToFPTI } from './fpti';
import { conditionallyFetchConversionData } from '@/lib/graphql/operations/currencyConversion';
import type {
  Amount,
  Cart,
  CartShippingMethod,
  Maybe,
  ShippingMethodType,
} from '@/generated/graphql';
import type { ShippingMethod } from '@/store/models/api/types';
import type { ShippingOption } from '@/lib/utils/shipping/types';

const conditionallyCalculateShippingAndTaxes = (): Promise<void> => {
  const { ui: uiActions } = appStore.getActions();
  const { setIsShippingShown, toggleIsPageLoading } = uiActions;
  const { api: apiState } = appStore.getState();
  if (!features.isActive('isSntSupported')) {
    logger.info('snt_not_supported');
    return Promise.resolve();
  }

  if (
    getSimpleShippingAddressString(
      simplifyShippingAddress(getShippingAddress(), getShippingCountry()),
    ) === apiState.calculatedShippingMethodsAddress
  ) {
    // Address hasn't changed since the last calculation, so no need to recalculate
    return Promise.resolve();
  }

  logFetchingShippingMethodsToFPTI();

  toggleIsPageLoading(true);

  const { api: apiStateBefore } = appStore.getState();
  const currValPreUpdateShipOpts =
    apiStateBefore?.cartDetails?.amounts?.total?.currencyValue;

  return (
    calculateShippingAndTaxes()
      .then(() => toggleIsPageLoading(false))
      // Turning the toggle off to execute updateShippingOptions
      // and conditionallyFetchConversionData in background.
      // If we call them in parallel, we will end up with unnecessary
      // calls to CURRENCY_CONVERSION_FROM_CREDIT_CARD_QUERY
      .then(() => updateShippingOptions())
      .then(() => {
        // DTOPPOR-1044: we need to re-fetch conversion data if
        // updateShippingOptions updates cart amount.
        const { api: apiStateAfter } = appStore.getState();
        const currValPostUpdateShipOpts =
          apiStateAfter?.cartDetails?.amounts?.total?.currencyValue;
        if (currValPostUpdateShipOpts !== currValPreUpdateShipOpts) {
          conditionallyFetchConversionData(null, true);
        }
        if (!features.isActive('hideShipping')) {
          setIsShippingShown(true);
        }
      })
      .catch(error => {
        // Log and noop if error is thrown
        logger.warn('calculate_shipping_and_taxes_error', { error });
      })
  );
};

type FormattedShippingMethod = {
  amount?: Maybe<Amount>;
  label: ReactNode[] | string;
  selected?: Maybe<boolean>;
  type?: Maybe<ShippingMethodType>;
  value?: Maybe<string>;
};

const formatShippingMethodProp = ({
  amount,
  id,
  label,
  selected,
  type,
  deliveryTime,
}: ShippingMethod): FormattedShippingMethod => {
  let shippingOptionLabel: ReactNode[] | string = getContent(
    'shipping.customLabelAndAmount',
    {
      amount: amount && amount.currencyFormatSymbolISOCurrency,
      label,
    },
  );

  if (features.isActive('isWps') && deliveryTime) {
    shippingOptionLabel = `${shippingOptionLabel} (${deliveryTime})`;
  }

  return {
    amount,
    label: shippingOptionLabel,
    selected,
    type,
    value: id,
  };
};

const updateSelectedShippingMethod = (id: string): void => {
  const { shippingMethods } = appStore.getState().api;
  const { setCartData } = appStore.getActions().api;
  const updatedShippingMethods = shippingMethods.map(method => {
    if (method) {
      method.selected = method.id === id;
    }
    return method;
  });
  setCartData({
    shippingMethods: updatedShippingMethods,
  });
};

type ShippingMethodTabs = {
  [key in ShippingMethodType]: {
    label: string;
    title: string;
    shippingMethods: FormattedShippingMethod[];
  };
};

const getHeaderContent = (shippingMethodTabs: ShippingMethodTabs): string => {
  const { SHIPPING, PICKUP } = shippingMethodTabs;
  if (SHIPPING && PICKUP) {
    return getContent('shipping.headers.shipOrPickup');
  }

  if (SHIPPING) {
    return getContent('shipping.headers.shipOnly');
  }

  return getContent('shipping.headers.pickupOnly');
};

const getShippingMethodTabs = (
  shippingMethods: NonNullable<Cart['shippingMethods']>,
): ShippingMethodTabs => {
  const isShippingCallbacksV2Eligible = features.isActive(
    'isShippingCallbacksV2Eligible',
  );
  return shippingMethods.reduce(
    (
      acc: ShippingMethodTabs,
      method: Maybe<CartShippingMethod>,
    ): ShippingMethodTabs => {
      if (!method || !method.type) {
        return acc;
      }

      const formattedMethod = formatShippingMethodProp(method);
      if (!acc[method.type]) {
        let contents = {
          label: '',
          title: '',
        };

        if (method.type === 'PICKUP') {
          // For V2 ShippingCallbacks we do not currently support PICKUP options
          // in AXO, therefore we're ignore all pickup shipping options
          // V2 ShippingCallbacks are only integrated for non-bopis merchants
          // Will be supported in a future iteration see Future work here:
          // https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=853380692#ShippingCallback(SPB)-FutureEffort
          if (isShippingCallbacksV2Eligible) {
            return acc;
          }
          contents = {
            label: getContent('shipping.choosePickupMethod'),
            title: getContent('buttons.shipping.pickup'),
          };
        } else if (method.type === 'SHIPPING') {
          contents = {
            label: getContent('shipping.chooseShippingMethod'),
            title: getContent('buttons.shipping.ship'),
          };
        }
        acc[method.type] = {
          ...contents,
          shippingMethods: [formattedMethod],
        };
      } else {
        acc[method.type].shippingMethods.push(formattedMethod);
      }
      return acc;
    },
    {} as ShippingMethodTabs,
  );
};

/**
 * Converts `Maybe<ShippingMethod>[]` to `ShippingOption[]` by filtering out
 * incomplete entries and ensuring all required fields are present.
 *
 * @param shippingMethods - Raw shipping methods from store (may contain nulls)
 * @returns Array of complete ShippingOption objects
 */
const toValidShippingOptions = (
  shippingMethods: Maybe<ShippingMethod>[] | undefined,
): ShippingOption[] => {
  return (
    shippingMethods
      ?.filter(
        (s): s is NonNullable<typeof s> =>
          s != null &&
          s.id != null &&
          s.type != null &&
          s.label != null &&
          s.selected !== null &&
          s.selected !== undefined,
      )
      .map(s => ({
        ...s,
        amount:
          s.amount &&
          s.amount.currencyCode != null &&
          s.amount.currencyValue != null
            ? {
                currencyCode: s.amount.currencyCode,
                currencyValue: s.amount.currencyValue,
              }
            : undefined,
        hasInsurance: s.hasInsurance,
        id: s.id!,
        insuranceAmount: s.insuranceAmount,
        label: s.label!,
        selected: s.selected!,
        type: s.type!,
      })) ?? []
  );
};

export {
  conditionallyCalculateShippingAndTaxes,
  formatShippingMethodProp,
  getHeaderContent,
  getShippingMethodTabs,
  ShippingMethodTabs,
  toValidShippingOptions,
  updateSelectedShippingMethod,
};
